/**
 * F.4 (ADR-0073) — pole „akceptant na następnym etapie" w oknie decyzji, liczone PO STRONIE
 * KLIENTA przed wysłaniem decyzji (`AcceptanceDialog`). Silnik (`apps/api/lib/processes/engine.ts`,
 * `resolveAddresseeForNode`) liczy to samo przy przetwarzaniu decyzji — tu robimy to na ZAMROŻONYM
 * snapshocie sprawy, żeby wiedzieć, CZY i DLA KTÓRYCH etapów pokazać pole, zanim user kliknie
 * „Wykonaj".
 *
 * Model WF: cel wynika z WYBRANEJ DROGI — listę dróg (z warunkami już policzonymi) podaje serwer
 * (`InstanceStep.availableRoutes`), więc klient nie zgaduje celu z krawędzi snapshotu. Dopóki droga
 * nie jest wybrana, nie ma celu i pole się nie pokazuje.
 */

import { addresseeFieldRule, type AvailableRoute, findDefaultRoute } from '@saas/shared';

import type { SnapshotEdge, SnapshotNode } from './instance-api';

/**
 * Zwraca id węzłów, które zostaną aktywowane po zatwierdzeniu wybraną drogą `route`
 * (`null` = droga jeszcze niewybrana → `[]`).
 *
 * - `parallel_split` rozgałęzia natychmiast — realnym celem (potencjalnie
 *   wymagającym adresata) są węzły ZA rozdzieleniem, nie sam węzeł kontrolny. Gałąź = etap
 *   docelowy drogi, nie sama droga (tak jak w silniku): dwie drogi rozdzielenia do tego samego
 *   etapu dają ten etap RAZ, w kolejności pierwszej drogi.
 */
export function resolveApproveTargetNodeIds(
  nodes: ReadonlyArray<SnapshotNode>,
  edges: ReadonlyArray<SnapshotEdge>,
  route: Pick<AvailableRoute, 'targetNodeId'> | null,
): string[] {
  if (!route?.targetNodeId) return [];

  const node = nodes.find((n) => n.id === route.targetNodeId);
  if (node?.nodeType !== 'parallel_split') return [route.targetNodeId];

  const branchTargetIds = edges
    .filter((e) => e.sourceNodeId === route.targetNodeId)
    .map((e) => e.targetNodeId)
    .filter((t): t is string => !!t);
  return [...new Set(branchTargetIds)];
}

/** Pole „akceptant na następnym etapie" dla JEDNEGO etapu docelowego wybranej drogi. */
export interface AddresseeField {
  /** Etap docelowy (węzeł decyzji ze snapshotu), na który przejdzie sprawa. */
  node: SnapshotNode;
  /**
   * `true` — etap ma „wymagaj wskazania": bez wybranej osoby decyzji nie da się wykonać.
   * `false` — wskazanie opcjonalne (etap w trybie „dowolny"): puste pole = cała pula etapu.
   */
  required: boolean;
  /**
   * Osoba podpowiadana (wstępnie wybrana) z konfiguracji etapu docelowego; pole wybiera ją
   * dopiero, gdy należy do pobranej puli. `null` = bez podpowiedzi.
   */
  defaultUserId: string | null;
}

/**
 * Czy pokazać pole „akceptant na następnym etapie" (jak w starym WF) — i dla których etapów,
 * czy jest wymagane i kogo podpowiedzieć — przy wybranej drodze `selectedRoute`.
 *
 * Pole pojawia się tylko, gdy decyzja faktycznie PRZESUWA sprawę dalej, czyli głos zamyka bieżący
 * etap: wybrana droga nie jest drogą domyślną etapu (`findDefaultRoute` — oznaczona albo jedyna;
 * głos za inną drogą zamyka etap zawsze) ALBO serwer policzył, że głos za drogą domyślną zamknie
 * etap (`defaultRouteVoteClosesStage` — w trybie „wszyscy" / „większość" zna próg głosów tylko on).
 *
 * Dla każdego etapu docelowego (za rozdzieleniem równoległym — każda gałąź osobno):
 *   - cel drogi to etap końcowy albo scalenie (każdy węzeł inny niż etap decyzji) → bez pola;
 *   - typ akceptantów jednoosobowy (właściciel rekordu, przełożony zgłaszającego, kierownik
 *     działu) albo brak konfiguracji → bez pola;
 *   - etap ma „wymagaj wskazania" (`requireAddresseeDesignation`) → pole wymagane (każdy tryb);
 *   - etap w trybie „dowolny" (`any`, także brak trybu — silnik przyjmuje wtedy `any`) bez flagi →
 *     pole opcjonalne (puste = cała pula);
 *   - etap w trybie „wszyscy" / „większość" bez flagi → bez pola (silnik odrzuca wtedy wskazanie
 *     jednej osoby: `ADDRESSEE_NOT_ALLOWED`).
 * Podpowiedź = `defaultAddresseeUserId` z konfiguracji etapu docelowego (o przynależności do puli
 * rozstrzyga pole po jej pobraniu).
 */
export function resolveAddresseeFields(params: {
  nodes: ReadonlyArray<SnapshotNode>;
  edges: ReadonlyArray<SnapshotEdge>;
  /** Wszystkie drogi etapu (`InstanceStep.availableRoutes`) — z nich wynika droga domyślna. */
  routes: ReadonlyArray<AvailableRoute>;
  /** Droga obowiązująca w oknie; `null` = jeszcze niewybrana. */
  selectedRoute: AvailableRoute | null;
  /** `InstanceStep.defaultRouteVoteClosesStage` kroku, na którym zapada decyzja. */
  defaultRouteVoteClosesStage: boolean;
}): AddresseeField[] {
  const { nodes, edges, routes, selectedRoute } = params;
  if (!selectedRoute || selectedRoute.targetIsEnd) return [];

  const isDefaultRoute = findDefaultRoute(routes)?.routeKey === selectedRoute.routeKey;
  const voteClosesStage = !isDefaultRoute || params.defaultRouteVoteClosesStage;
  if (!voteClosesStage) return [];

  const fields: AddresseeField[] = [];
  for (const nodeId of resolveApproveTargetNodeIds(nodes, edges, selectedRoute)) {
    const node = nodes.find((n) => n.id === nodeId);
    const field = node ? addresseeFieldForNode(node) : null;
    if (field) fields.push(field);
  }
  return fields;
}

/**
 * Reguły pola dla pojedynczego etapu docelowego (patrz `resolveAddresseeFields`): cel inny niż
 * etap decyzji → bez pola; reszta — wspólna reguła z silnikiem (`addresseeFieldRule` z
 * `@saas/shared`; silnik podstawia według niej domyślnego adresata przy decyzji hurtowej). Typy
 * jednoosobowe (właściciel rekordu, przełożony zgłaszającego, kierownik działu) nigdy nie dostają
 * pola — dlatego procesy z wzorów ITE (wyłącznie takie typy) go nie pokazują.
 */
function addresseeFieldForNode(node: SnapshotNode): AddresseeField | null {
  if (node.nodeType !== 'decision') return null;
  const rule = addresseeFieldRule(node.approverConfig);
  return rule ? { node, ...rule } : null;
}
